import React, { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { PAContext } from "../../../Services/PAContext";
import { getAllPhotosForEvent } from '../../../Services/BrowserDatabaseFunctions';
import './photos.css';

import {
    Avatar,
    Button,
    Card, CardMedia,
    Chip,
    Dialog, DialogActions,
    DialogContent, DialogTitle, Paper,
    Switch, TextField, ToggleButton, ToggleButtonGroup
} from "@mui/material";
import Badge from "@mui/material/Badge";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import { formatName, stringAvatar, stringToColor } from "../../Helpers/TextStyling";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Stack } from "@mui/system";
import { getAllContactsFromDatabase } from "../../Restricted/Contacts/BrowserDatabaseFunctions";

const PhotoFlipCard = ({ item, Account, handleOpenImage, onDeletePhoto, sendSocketMessage }) => {
    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();
    const [isFlipped, setIsFlipped] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const [draft, setDraft] = useState({ ...item });
    const [emailInput, setEmailInput] = useState("");

    useEffect(() => {
        if (!isFlipped) {
            setDraft({ ...item });
        }
    }, [item, isFlipped]);

    // Fallback order: Explicit displaySrc -> local full_data -> local thumbnail_data -> signed_url -> placeholder
    const imageSrc = item.displaySrc
        || item.full_data
        || item.thumbnail_data
        || item.signed_url
        || "https://plannedadventure.com/assets/images/user_bg1.png";

    const updateDraft = (updates) => {
        setDraft(prev => ({ ...prev, ...updates }));
    };

    const handleAddEmail = (e) => {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        const email = emailInput.trim().toLowerCase();
        const currentEmails = draft.share_with || [];
        if (email && !currentEmails.includes(email)) {
            updateDraft({
                share_with: [...currentEmails, email],
                sharable: true
            });
            setEmailInput("");
        }
    };

    const handleRemoveEmail = (email, e) => {
        if (e) e.stopPropagation();
        const newList = (draft.share_with || []).filter(e => e !== email);
        updateDraft({ share_with: newList, sharable: newList.length > 0 });
    };

    const handleFinalSave = () => {
        set_PA_GlobalObject((prev) => {
            const currentPhotos = prev.Planning?.photos || [];
            const newPhotos = currentPhotos.map(p =>
                ((p.id && p.id === item.id) || (p.temp_id && p.temp_id === item.temp_id))
                    ? { ...p, ...draft }
                    : p
            );
            return { ...prev, Planning: { ...prev.Planning, photos: newPhotos } };
        });

        if (PA_GlobalObject.Planning?.status !== 10) {
            sendSocketMessage({
                action: "E_updateEventPhotoMetadata",
                photo_id: item.id,
                temp_id: item.temp_id,
                updates: {
                    caption: draft.caption,
                    photo_album: draft.photo_album,
                    private: draft.private,
                    share_with: draft.share_with,
                    sharable: draft.sharable
                },
                event_id: PA_GlobalObject.Planning?.id
            });
        }

        setIsFlipped(false);
    };

    return (
        <Box sx={{ position: 'relative', minHeight: '400px', width: '100%', perspective: '1200px' }}>
            <Box sx={{
                position: 'relative', width: '100%', height: '100%',
                transition: 'transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                transformStyle: 'preserve-3d',
                transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
            }}>
                <Card sx={{
                    position: 'absolute', inset: 0, backfaceVisibility: 'hidden',
                    borderRadius: '28px', border: item.photo_album ? '3px solid #f09637' : '1px solid #eef2f6',
                    display: 'flex', flexDirection: 'column', bgcolor: '#fff', overflow: 'hidden'
                }}>
                    {item.photo_album && (
                        <Box sx={{
                            position: 'absolute', top: 16, left: 16, zIndex: 10, bgcolor: '#f09637',
                            color: '#fff', p: '6px 14px', borderRadius: '12px', fontSize: '11px',
                            fontWeight: '900', boxShadow: '0 4px 10px rgba(0,0,0,0.2)', pointerEvents: 'none'
                        }}>
                            <i className="icon-star-filled" style={{ marginRight: '6px' }} /> COVER
                        </Box>
                    )}
                    <CardMedia component="img" image={imageSrc} height="260" sx={{ objectFit: 'cover', cursor: 'pointer' }} onClick={() => handleOpenImage(imageSrc)} />
                    <Box sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#2c3e50' }}>{item.caption || "Adventure moment..."}</Typography>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box display="flex" gap={1}>
                                <i className={item.private ? "icon-lock" : "icon-users"} style={{ color: item.private ? "#e74c3c" : "#27ae60" }} />
                                {item.share_with?.length > 0 && <Typography variant="caption" sx={{ fontWeight: 800 }}>{item.share_with.length} Shared</Typography>}
                            </Box>
                            <IconButton onClick={() => setIsFlipped(true)} size="small" sx={{ bgcolor: '#f0f7ff', color: '#428ce2' }}><i className="icon-pencil" /></IconButton>
                        </Box>
                    </Box>
                </Card>

                <Card sx={{
                    position: 'absolute', inset: 0, backfaceVisibility: 'hidden',
                    transform: 'rotateY(180deg)', borderRadius: '28px', p: 3,
                    border: '3px solid #428ce2', display: 'flex', flexDirection: 'column', bgcolor: '#fff'
                }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 900 }}>Settings</Typography>
                        <IconButton onClick={() => setConfirmOpen(true)} sx={{ color: '#e74c3c', bgcolor: '#fff5f5' }}>
                            <i className="icon-trash" />
                        </IconButton>
                    </Box>

                    <Stack spacing={2} sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
                        <TextField
                            label="Caption" fullWidth multiline rows={2} variant="filled"
                            value={draft.caption || ""}
                            onChange={(e) => updateDraft({ caption: e.target.value })}
                            InputProps={{ disableUnderline: true, sx: { borderRadius: '12px' } }}
                        />

                        <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: draft.photo_album ? '#f0963710' : '#f8f9fa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>Show on Cover</Typography>
                            <Switch checked={!!draft.photo_album} color="warning" onChange={(e) => updateDraft({ photo_album: e.target.checked })} />
                        </Box>

                        <ToggleButtonGroup
                            fullWidth size="small" value={draft.private ? 'PRIV' : 'GRP'} exclusive
                            onChange={(e, v) => v && updateDraft({ private: v === 'PRIV' })}
                            sx={{ bgcolor: '#f1f5f9', p: '4px', borderRadius: '12px' }}
                        >
                            <ToggleButton value="GRP" sx={{ border: 'none', borderRadius: '10px !important', fontWeight: 700 }}>Group</ToggleButton>
                            <ToggleButton value="PRIV" sx={{ border: 'none', borderRadius: '10px !important', fontWeight: 700 }}>Private</ToggleButton>
                        </ToggleButtonGroup>

                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>SHARE VIA EMAIL</Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                <TextField
                                    fullWidth size="small" placeholder="Enter email..."
                                    value={emailInput} onChange={(e) => setEmailInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddEmail())}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                />
                                <IconButton onClick={handleAddEmail} sx={{ bgcolor: '#428ce2', color: '#fff' }}><i className="icon-plus" /></IconButton>
                            </Box>
                            <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {(draft.share_with || []).map((email, i) => (
                                    <Chip key={i} label={email} onDelete={(e) => handleRemoveEmail(email, e)} sx={{ bgcolor: '#f0f7ff', color: '#428ce2', fontWeight: 700, borderRadius: '8px' }} />
                                ))}
                            </Box>
                        </Box>
                    </Stack>

                    <Button
                        fullWidth variant="contained"
                        onClick={handleFinalSave}
                        sx={{ mt: 2, borderRadius: '14px', py: 1.5, fontWeight: 900, bgcolor: '#428ce2' }}
                    >
                        Done
                    </Button>
                </Card>
            </Box>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 900, color: '#2c3e50' }}>Delete Memory?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary">This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={() => setConfirmOpen(false)} sx={{ color: '#94a3b8', fontWeight: 700 }}>Keep it</Button>
                    <Button variant="contained" color="error" onClick={() => { onDeletePhoto(item); setConfirmOpen(false); }} sx={{ borderRadius: '10px', fontWeight: 800 }}>Delete Forever</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const MemoizedPhotoCard = React.memo(PhotoFlipCard, (prevProps, nextProps) => {
    return (
        prevProps.item.caption === nextProps.item.caption &&
        prevProps.item.photo_album === nextProps.item.photo_album &&
        prevProps.item.private === nextProps.item.private &&
        prevProps.item.share_with?.length === nextProps.item.share_with?.length &&
        prevProps.item.displaySrc === nextProps.item.displaySrc &&
        prevProps.item.signed_url === nextProps.item.signed_url &&
        prevProps.Account?.id === nextProps.Account?.id
    );
});

function PhotosList(props) {
    const navigate = useNavigate();
    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();
    const { dataArray, sendSocketMessage } = useContext(PAContext);
    const { Account, SampleAttendees, authenticated } = dataArray;

    const [dbContacts, setDbContacts] = useState([]);

    useEffect(() => {
        if (!authenticated) {
            setDbContacts([]);
            return;
        }

        let cancelled = false;
        getAllContactsFromDatabase()
            .then(contacts => {
                if (cancelled) return;
                setDbContacts(Array.isArray(contacts) ? contacts : []);
            })
            .catch(err => {
                console.error('Failed to load Contacts from browser DB:', err);
                if (!cancelled) setDbContacts([]);
            });

        return () => { cancelled = true; };
    }, [authenticated]);

    const [attendeesReferenceArray, setAttendeesReferenceArray] = useState(authenticated ? dbContacts : SampleAttendees);

    useEffect(() => {
        setAttendeesReferenceArray(authenticated ? dbContacts : SampleAttendees);
    }, [authenticated, dbContacts, SampleAttendees]);

    const [selectedAttendee, setSelectedAttendee] = useState(Account.id);
    const selectedChipRef = useRef(null);

    const planningAttendee = usePA_GlobalStore
        .getState()
        .PA_GlobalObject
        .Planning
        ?.attendees
        ?.find((p) => p.id === Account.id);

    const isPartialAccount = planningAttendee?.role === "partial";

    // Direct binding from global store photos
    const globalPhotos = usePA_GlobalStore(state => state.PA_GlobalObject.Planning?.photos) || [];
    const [eventPhotos, setEventPhotos] = useState(globalPhotos);

    useEffect(() => {
        const updatePhotos = async () => {
            const planning = PA_GlobalObject?.Planning;
            if (!planning?.photos) return;

            let visiblePhotos = [];
            if (isPartialAccount) {
                const lodgingPhotoIds = planning.lodging?.flatMap(l =>
                    (l.attendees?.some(att => att.id === Account.id) && l.attendee_photos) ? l.attendee_photos.map(p => p.id) : []
                ) || [];
                const mealPhotoIds = planning.meals?.flatMap(m =>
                    (m.attendees?.some(att => att.id === Account.id) && m.attendee_photos) ? m.attendee_photos.map(p => p.id) : []
                ) || [];
                const activityPhotoIds = planning.activities?.flatMap(a =>
                    (a.attendees?.some(att => att.id === Account.id) && a.attendee_photos) ? a.attendee_photos.map(p => p.id) : []
                ) || [];
                const eventPhotoIds = planning.photos?.filter(p => p.attendee_id === Account.id).map(p => p.id) || [];

                const uniquePhotoIds = new Set([...lodgingPhotoIds, ...mealPhotoIds, ...activityPhotoIds, ...eventPhotoIds]);
                visiblePhotos = planning.photos.filter(p => uniquePhotoIds.has(p.id));
            } else {
                visiblePhotos = planning.photos;
            }

            const missingUrlsBatch = [];

            const processed = visiblePhotos.map((item) => {
                // Ensure local data is NOT hidden during batch background fetch
                const localData = item.full_data || item.thumbnail_data || item.preview;
                const isLocal = localData?.startsWith('blob:') || localData?.startsWith('data:');

                if (isLocal) {
                    return { ...item, displaySrc: localData };
                }

                if (item.signed_url) {
                    return { ...item, displaySrc: item.signed_url };
                }

                if (item.gcs_path && !item.signed_url) {
                    missingUrlsBatch.push({
                        photo_id: item.id || item.temp_id,
                        temp_id: item.temp_id,
                        gcs_path: item.gcs_path
                    });
                    return { ...item, displaySrc: localData || null, isPendingUrl: true };
                }

                return { ...item, displaySrc: localData || null, isUploading: !item.gcs_path };
            });

            if (missingUrlsBatch.length > 0 && planning.status !== 10) {
                sendSocketMessage({
                    action: "E_requestBatchSignedUrlsForGCS",
                    event_id: planning.id,
                    photos: missingUrlsBatch,
                    is_thumbnail: false
                });
            }

            setEventPhotos(processed);
        };

        updatePhotos();
    }, [globalPhotos, PA_GlobalObject?.Planning?.id, authenticated]);

    const handleChipClick = (_attendee_id) => {
        setSelectedAttendee(_attendee_id);
    };

    useEffect(() => {
        if (selectedChipRef.current) {
            selectedChipRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center',
            });
        }
    }, [selectedAttendee]);

    const attendeePhotosCount = (usePA_GlobalStore.getState().PA_GlobalObject.Planning?.attendees || []).map((att) => ({
        ...att,
        photoCount: (globalPhotos).reduce((count, item) => count + ((item.attendee_id === att.id || (!item.attendee_id && att.id === Account.id)) ? 1 : 0), 0),
    }));

    const sortedAttendees = attendeePhotosCount.sort((a, b) => {
        if (b.photoCount === a.photoCount) {
            return a.fname.localeCompare(b.fname);
        }
        return b.photoCount - a.photoCount;
    });

    function handlePhotosAddMenuClick() {
        navigate('/Planner/Photos/AddNew');
    }

    const [openImage, setOpenImage] = useState(false);
    const [imageSrc, setImageSrc] = useState("");

    const handleCloseImage = () => {
        setOpenImage(false);
    };

    const handleOpenImage = React.useCallback((src) => {
        setImageSrc(src);
        setOpenImage(true);
    }, []);

    const handleDeletePhoto = React.useCallback((photo) => {
        set_PA_GlobalObject((prev) => {
            const currentPhotos = prev.Planning?.photos || [];
            const updatedPhotos = currentPhotos.filter(p =>
                (p.id && p.id !== photo.id) || (p.temp_id && p.temp_id !== photo.temp_id)
            );

            return {
                ...prev,
                Planning: {
                    ...prev.Planning,
                    photos: updatedPhotos
                }
            };
        });

        if (PA_GlobalObject.Planning?.status !== 10) {
            sendSocketMessage({
                action: "E_deleteEventPhoto",
                photo_id: photo.id,
                temp_id: photo.temp_id,
                event_id: PA_GlobalObject.Planning?.id
            });
        }
    }, [authenticated, sendSocketMessage]);

    return (
        <div className="photos-list-container">
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.6)',
                    boxShadow: '0 4px 24px -6px rgba(0,0,0,0.06)',
                    borderRadius: '12px 12px 0 0',
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                            background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)',
                            p: 0.9,
                            borderRadius: '10px',
                            display: 'flex',
                            boxShadow: '0 4px 10px rgba(99, 102, 241, 0.3)'
                        }}>
                            <i className="icon-picture2" style={{ color: 'white', fontSize: '18px' }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h6" sx={{
                                fontWeight: 800,
                                color: '#1e293b',
                                fontSize: '1rem',
                                letterSpacing: -0.4,
                                lineHeight: 1.2
                            }}>
                                Photos
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                <i className="icon-camera3" style={{ color: '#6366f1', fontSize: '10px' }} />
                                <Typography variant="caption" sx={{
                                    color: '#64748b',
                                    fontWeight: 700,
                                    fontSize: '0.65rem',
                                    letterSpacing: 0.1
                                }}>
                                    Shared Memories
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handlePhotosAddMenuClick}
                            startIcon={<i className="icon-plus" style={{ fontSize: '12px' }} />}
                            sx={{
                                bgcolor: '#4338ca',
                                color: 'white',
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                px: 2,
                                boxShadow: '0 4px 12px rgba(67, 56, 202, 0.2)',
                                '&:hover': {
                                    bgcolor: '#3730a3',
                                    boxShadow: '0 6px 16px rgba(67, 56, 202, 0.3)',
                                }
                            }}
                        >
                            Add
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {!isPartialAccount && (
                <Box
                    className="photos-list-attendee-list-container"
                    sx={{
                        display: 'flex',
                        overflowX: 'auto',
                        msOverflowStyle: 'none',
                        alignItems: 'flex-start'
                    }}
                >
                    {sortedAttendees.length > 0 ? (
                        sortedAttendees.map((attendee) => {
                            const attendeeAvatar = attendeesReferenceArray.find((_att) => _att.id === attendee.id);
                            const isSelected = selectedAttendee === attendee.id;

                            const visiblePhotoCount = globalPhotos.reduce((count, item) => {
                                const photoOwnerId = item.attendee_id || Account.id;
                                const isOwner = photoOwnerId === Account.id;
                                const isPublic = item.private === false;
                                const isAttendee = photoOwnerId === attendee.id;
                                return count + (isAttendee && (isOwner || isPublic) ? 1 : 0);
                            }, 0);

                            const fullName = attendee.nickname?.trim()
                                ? formatName(attendee.nickname)
                                : `${formatName(attendee.fname)}`;

                            return (
                                <Box
                                    key={attendee.id}
                                    onClick={() => handleChipClick(attendee.id)}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        position: 'relative',
                                        minWidth: '60px',
                                        transition: 'transform 0.2s ease',
                                    }}
                                >
                                    <Badge
                                        badgeContent={visiblePhotoCount > 0 ? visiblePhotoCount : null}
                                        sx={{
                                            '& .MuiBadge-badge': {
                                                backgroundColor: '#eef2ff',
                                                color: '#4338ca',
                                                fontSize: '10px',
                                                fontWeight: 900,
                                                border: '2px solid #fff',
                                                top: 5,
                                                right: 5
                                            },
                                        }}
                                    >
                                        <Box sx={{
                                            p: '3px',
                                            borderRadius: '50%',
                                            border: isSelected ? '2px solid #151269' : '2px solid transparent',
                                            transition: 'all 0.3s ease'
                                        }}>
                                            {!attendeeAvatar?.avatar?.preview ? (
                                                <Avatar
                                                    {...stringAvatar(fullName)}
                                                    sx={{
                                                        width: 30,
                                                        height: 30,
                                                        fontSize: '16px',
                                                        fontWeight: 600,
                                                        boxShadow: isSelected ? '0 4px 10px rgba(21, 18, 105, 0.2)' : 'none',
                                                        border: `1px solid ${stringToColor(fullName)}`
                                                    }}
                                                />
                                            ) : (
                                                <Avatar
                                                    src={attendeeAvatar.avatar.preview}
                                                    sx={{
                                                        width: 30,
                                                        height: 30,
                                                        boxShadow: isSelected ? '0 4px 10px rgba(21, 18, 105, 0.2)' : 'none',
                                                        border: `1px solid ${stringToColor(fullName)}`
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </Badge>

                                    <Typography sx={{
                                        fontSize: '11px',
                                        fontWeight: isSelected ? 800 : 500,
                                        mt: 1,
                                        color: isSelected ? '#151269' : '#666',
                                        textAlign: 'center',
                                        maxWidth: '70px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {attendee.nickname || attendee.fname}
                                    </Typography>
                                </Box>
                            );
                        })
                    ) : (
                        <Box sx={{ p: 2 }}>
                            <Typography variant="caption" color="textSecondary">No attendees found</Typography>
                        </Box>
                    )}
                </Box>
            )}

            <div className="photos-list-items-container">
                {(() => {
                    const displayPhotos = eventPhotos.filter(p => {
                        const ownerId = p.attendee_id || Account.id;
                        return ownerId === selectedAttendee && (ownerId === Account.id || !p.private);
                    });

                    if (displayPhotos.length > 0) {
                        return (
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                gap: '24px',
                                padding: '16px'
                            }}>
                                {displayPhotos.map((photo) => (
                                    <MemoizedPhotoCard
                                        key={photo.id || photo.temp_id}
                                        item={photo}
                                        Account={Account}
                                        handleOpenImage={handleOpenImage}
                                        onDeletePhoto={handleDeletePhoto}
                                        sendSocketMessage={sendSocketMessage}
                                    />
                                ))}
                            </Box>
                        );
                    }

                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 10,
                                px: 3,
                                mt: 4,
                                mx: 'auto',
                                maxWidth: '400px',
                                borderRadius: '24px',
                                background: 'rgba(255, 255, 255, 0.6)',
                                backdropFilter: 'blur(10px)',
                                border: '2px dashed #e0e7ff',
                                textAlign: 'center',
                            }}
                        >
                            <Box
                                sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mb: 3,
                                    boxShadow: '0 10px 20px -5px rgba(99, 102, 241, 0.15)',
                                }}
                            >
                                <i className="icon-camera" style={{ fontSize: '36px', color: '#6366f1' }} />
                            </Box>

                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 800,
                                    color: '#1e293b',
                                    mb: 1,
                                    letterSpacing: '-0.02em'
                                }}
                            >
                                No shared moments yet
                            </Typography>

                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#64748b',
                                    lineHeight: 1.6,
                                    fontWeight: 500,
                                    mb: 4
                                }}
                            >
                                Every great trip has a story to tell. Be the first to start the gallery!
                            </Typography>

                            <Button
                                variant="contained"
                                startIcon={<i className="icon-plus" style={{ fontSize: '12px' }} />}
                                onClick={handlePhotosAddMenuClick}
                                sx={{
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    bgcolor: '#6366f1',
                                    px: 3,
                                    py: 1,
                                    boxShadow: '0 4px 14px rgba(99, 102, 241, 0.39)',
                                    '&:hover': {
                                        bgcolor: '#4f46e5',
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 6px 20px rgba(99, 102, 241, 0.23)',
                                    }
                                }}
                            >
                                Upload Photos
                            </Button>
                        </Box>
                    );
                })()}
            </div>

            <Dialog open={openImage} onClose={handleCloseImage} maxWidth="md" fullWidth>
                <DialogContent sx={{ textAlign: "center" }}>
                    <img src={imageSrc} alt="Photo" style={{ maxWidth: "100%", maxHeight: "80vh" }} />
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default PhotosList;